import { db } from '@/features/offline/db'
import { baseUrl } from '@/services/catalog'

/**
 * An- und Abmeldung für Push-Benachrichtigungen.
 *
 * Der Weg für die aktive Meldung: Ohne ihn erfährt man von einer neuen Karte erst
 * beim nächsten Öffnen der App.
 *
 * Die Systemabfrage des Browsers wird **ausschließlich** auf ein bewusstes Ja hin
 * ausgelöst — durch den Schalter in den Einstellungen oder durch die einmalige Frage
 * beim Start (`sollPushFragen()` unten, dargestellt von `PushPrompt.tsx`). Nie von
 * selbst: Eine ungefragte Systemabfrage wird weggewischt, und danach lässt sie sich
 * nur noch über die Geräteeinstellungen zurückholen. Ein verschenkter Versuch ist ein
 * endgültig verschenkter.
 */

export type PushZustand =
  /** Aufruf ohne HTTPS. Dann fehlt die Schnittstelle auf **jedem** Gerät. */
  | 'kein-https'
  /** Läuft im Browser, obwohl das Gerät Push nur in der installierten App zulässt (iOS). */
  | 'nur-als-app'
  /** Das Gerät kann keine Push-Nachrichten empfangen. */
  | 'nicht-unterstuetzt'
  /** Auf diesem Server ist kein Benachrichtigungsdienst eingerichtet. */
  | 'nicht-eingerichtet'
  /** Der Nutzer hat die Erlaubnis abgelehnt; zurücknehmen lässt sich das nur im Gerät. */
  | 'verweigert'
  | 'aus'
  | 'an'

const DIENST = () => new URL('api/push.php', baseUrl)

function unterstuetzt(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * Warum Push hier nicht geht — oder `null`, wenn es geht.
 *
 * Die Unterscheidung ist der ganze Zweck dieser Funktion: Alle drei Fälle sehen im
 * Browser gleich aus (`PushManager` fehlt), haben aber völlig verschiedene Ursachen.
 * Wer die App über eine Adresse ohne HTTPS aufruft, kann sie zum Startbildschirm
 * hinzufügen, so oft er will — es ändert nichts. Ein Hinweis, der in die falsche
 * Richtung schickt, ist schlimmer als keiner.
 */
function hindernis(): 'kein-https' | 'nur-als-app' | 'nicht-unterstuetzt' | null {
  // Zuerst, weil es alles andere überdeckt: Ohne sicheren Kontext entfernt der Browser
  // Service Worker und Push-Schnittstelle vollständig. Ausnahme ist localhost, das
  // als sicher gilt — sonst ließe sich nicht entwickeln.
  if (!window.isSecureContext) return 'kein-https'
  if (unterstuetzt()) return null

  // Die Schnittstelle fehlt trotz HTTPS. Auf iPhone und iPad ist das der Normalfall,
  // solange die App im Browser läuft; sie erscheint erst in der vom Startbildschirm
  // gestarteten App.
  return istInstalliert() ? 'nicht-unterstuetzt' : 'nur-als-app'
}

/**
 * Läuft die App als installierte App und nicht im Browser?
 *
 * Auf iPhone und iPad gibt es Push-Nachrichten nur in der vom Startbildschirm
 * gestarteten App. Im Browser fehlt die Schnittstelle ersatzlos — dort hilft nur der
 * Hinweis, die App zuerst zu installieren.
 */
function istInstalliert(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/** Holt den öffentlichen Schlüssel des Servers; `null`, wenn dort nichts eingerichtet ist. */
async function serverSchluessel(): Promise<string | null> {
  try {
    const antwort = await fetch(DIENST(), { cache: 'no-store' })
    if (!antwort.ok) return null
    const daten = (await antwort.json()) as { publicKey?: unknown }
    return typeof daten.publicKey === 'string' ? daten.publicKey : null
  } catch {
    // Kein Netz oder kein PHP auf diesem Server.
    return null
  }
}

export async function holePushZustand(): Promise<PushZustand> {
  const grund = hindernis()
  if (grund !== null) return grund
  if ((await serverSchluessel()) === null) return 'nicht-eingerichtet'
  if (Notification.permission === 'denied') return 'verweigert'

  const registrierung = await navigator.serviceWorker.ready
  return (await registrierung.pushManager.getSubscription()) ? 'an' : 'aus'
}

export async function schaltePushEin(): Promise<PushZustand> {
  const grund = hindernis()
  if (grund !== null) return grund

  const schluessel = await serverSchluessel()
  if (schluessel === null) return 'nicht-eingerichtet'

  const erlaubnis = await Notification.requestPermission()
  if (erlaubnis !== 'granted') return erlaubnis === 'denied' ? 'verweigert' : 'aus'

  const registrierung = await navigator.serviceWorker.ready
  const abo =
    (await registrierung.pushManager.getSubscription()) ??
    (await registrierung.pushManager.subscribe({
      // Pflicht in allen Browsern: Jede Push-Nachricht muss zu einer sichtbaren
      // Meldung führen. Stille Nachrichten zur Hintergrundarbeit sind nicht erlaubt.
      userVisibleOnly: true,
      applicationServerKey: schluesselAlsBytes(schluessel),
    }))

  const antwort = await fetch(DIENST(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: abo.endpoint }),
  })

  if (!antwort.ok) {
    // Das Abo beim Browser wieder zurücknehmen: Ein Abo, das der Server nicht kennt,
    // empfängt nie etwas — der Schalter stünde auf „an" und täuschte eine
    // Benachrichtigung vor, die nie käme.
    await abo.unsubscribe()
    throw new Error('Die Anmeldung konnte auf dem Server nicht gespeichert werden.')
  }

  return 'an'
}

export async function schaltePushAus(): Promise<PushZustand> {
  const grund = hindernis()
  if (grund !== null) return grund

  const registrierung = await navigator.serviceWorker.ready
  const abo = await registrierung.pushManager.getSubscription()
  if (!abo) return 'aus'

  // Erst beim Server abmelden, dann beim Browser. Andersherum bliebe eine tote
  // Adresse in der Liste stehen, an die bis zu ihrem ersten Fehlschlag weiter
  // gesendet würde.
  try {
    await fetch(DIENST(), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: abo.endpoint }),
    })
  } catch {
    // Ohne Netz nicht zu ändern. Der Server räumt die Adresse selbst weg, sobald der
    // Push-Dienst sie als abgemeldet meldet.
  }

  await abo.unsubscribe()
  return 'aus'
}

/**
 * Der Schlüssel kommt als Base64 in der URL-sicheren Form; die Schnittstelle des
 * Browsers erwartet die Rohbytes.
 *
 * Die Spezifikation erlaubt zwar auch, den Text direkt zu übergeben, aber nicht jede
 * ältere Fassung von Safari setzt das um — und die App soll auch auf betagten
 * Diensthandys laufen. Der Rückgabetyp nennt den Puffer ausdrücklich, sonst gilt er
 * als möglicherweise geteilter Speicher und passt nicht zur Schnittstelle.
 */
function schluesselAlsBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const roh = atob(base64)

  return Uint8Array.from(roh, (zeichen) => zeichen.charCodeAt(0))
}

// ─── Die einmalige Frage beim Start ──────────────────────────────────────────────

const GEFRAGT_KEY = 'push:gefragt'

/**
 * Soll die App von sich aus nach Benachrichtigungen fragen?
 *
 * Nur wenn es auch etwas zu entscheiden gibt: Der Dienst muss erreichbar, das Gerät
 * fähig und noch nichts eingestellt sein. Und nur ein einziges Mal — wer abgelehnt
 * hat, wird nicht bei jedem Start erneut gefragt; der Schalter in den Einstellungen
 * bleibt jederzeit erreichbar.
 *
 * Bewusst nicht an den allerersten Start gebunden, sondern an den ersten Start, an dem
 * es möglich ist: Wird der Dienst erst später eingerichtet, bekommen auch die längst
 * installierten Apps die Frage noch.
 */
export async function sollPushFragen(): Promise<boolean> {
  if (await wurdeGefragt()) return false

  return (await holePushZustand()) === 'aus'
}

async function wurdeGefragt(): Promise<boolean> {
  const eintrag = await db.settings.get(GEFRAGT_KEY)

  return eintrag?.value === true
}

/** Hält fest, dass die Frage gestellt wurde — gleich wie sie ausgegangen ist. */
export async function merkeGefragt(): Promise<void> {
  await db.settings.put({ key: GEFRAGT_KEY, value: true })
}
